import { Component, inject, signal, computed, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageModule } from 'primeng/message';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AccountsStore, CurrenciesStore } from '@app/ui/stores';
import { ERROR_HANDLER_PORT, NOTIFICATION_PORT } from '@app/ui/shared';
import { type MonobankAccount } from '../monobank-api.models';
import { MonobankApiService } from '../monobank-api.service';
import { MonobankSettingsService } from '../monobank-settings.service';
import type { MonobankAccountMapping } from '../monobank-settings.models';

export type { MonobankAccountMapping } from '../monobank-settings.models';

@Component({
  selector: 'app-monobank-account-mapping-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    SelectModule,
    ProgressSpinnerModule,
    MessageModule,
    TranslateModule,
  ],
  template: `
    @if (loading()) {
      <div class="flex flex-1 justify-center items-center p-8">
        <p-progressspinner />
      </div>
    } @else if (errorMessage()) {
      <p-message severity="error" styleClass="w-full">
        {{ errorMessage() }}
      </p-message>
    } @else {
      <p class="text-sm text-color-secondary text-center my-4">
        {{ 'screens.monobank.labels.account_mapping_description' | translate }}
      </p>
      <div class="flex-1 overflow-auto scroll-container flex flex-col gap-2 px-4">
        @for (account of sortedMonobankAccounts(); track account.id) {
          <div class="border border-stone-500 rounded p-4 scroll-item">
            <div class="flex flex-col gap-3">
              <div>
                <div class="text-xs mb-1">
                  {{ account.iban }}
                </div>
                <div class="text-xs text-color-secondary">
                  {{ currencyCodeToName().get(account.currencyCode) || account.currencyCode }}
                  •
                  {{ account.type }}
                  •
                  {{ account.maskedPan.length > 0 ? account.maskedPan[0] : account.iban }}
                </div>
              </div>

              <p-select
                [options]="appAccounts()"
                [ngModel]="accountMapping()[account.id]?.accountId"
                (ngModelChange)="onAccountChange(account.id, account.currencyCode, $event)"
                [ngModelOptions]="{ standalone: true }"
                optionLabel="name"
                optionValue="id"
                [placeholder]="'screens.monobank.labels.no_association' | translate"
                class="w-full"
                [showClear]="true"
                [disabled]="!currencyCodeToName().has(account.currencyCode)"
              />
              @if (!currencyCodeToName().has(account.currencyCode)) {
                <small class="text-orange-500">
                  {{ 'screens.monobank.labels.currency_not_registered' | translate : { code: account.currencyCode } }}
                </small>
              }
            </div>
          </div>
        }
      </div>
    }

    <div class="p-dialog-form-footer">
      <p-button
        type="button"
        severity="danger"
        [outlined]="true"
        [label]="'common.actions.disable' | translate"
        (onClick)="onDisable()"
      />
      <p-button
        type="button"
        severity="primary"
        [outlined]="true"
        [label]="'common.actions.ok' | translate"
        [disabled]="loading() || !!errorMessage()"
        (onClick)="onSave()"
      />
    </div>
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      margin: -1rem;
    }

    .scroll-container {
      scroll-snap-type: y proximity;
    }

    .scroll-item {
      scroll-snap-align: end;
      scroll-snap-stop: normal;
    }

    :host ::ng-deep .p-select.w-full .p-overlay-content {
      width: auto;
    }
  `,
})
export class MonobankAccountMappingDialogComponent implements OnInit {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly errorHandler = inject(ERROR_HANDLER_PORT);
  private readonly monobankApi = inject(MonobankApiService);
  private readonly state = inject(MonobankSettingsService);
  private readonly accountsStore = inject(AccountsStore);
  private readonly currenciesStore = inject(CurrenciesStore);
  private readonly notifications = inject(NOTIFICATION_PORT);
  private readonly translate = inject(TranslateService);

  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly monobankAccounts = signal<MonobankAccount[]>([]);
  readonly sortedMonobankAccounts = computed(() => {
    return [...this.monobankAccounts()].sort((a, b) => {
      const aIsFop = a.type === 'fop' ? 1 : 0;
      const bIsFop = b.type === 'fop' ? 1 : 0;

      if (aIsFop !== bIsFop) {
        return aIsFop - bIsFop;
      }

      return (
        0 +
        100 * a.type.localeCompare(b.type) +
        10 * Math.sign(a.currencyCode - b.currencyCode) +
        1 * (a.iban || a.id).localeCompare(b.iban || b.id)
      );
    });
  });
  readonly accountMapping = signal<Record<string, MonobankAccountMapping>>({});
  readonly appAccounts = computed(() => this.accountsStore.displayAccounts());
  readonly currencyCodeToName = computed(() => {
    const map = new Map<number, string>();
    this.currenciesStore.currencies().forEach((currency) => {
      const isoCode = parseInt(currency.isoCode, 10);
      if (!isNaN(isoCode)) {
        map.set(isoCode, currency.code);
      }
    });
    return map;
  });

  private getAppCurrencyCode(isoCode: number): string {
    return this.currencyCodeToName().get(isoCode) || isoCode.toString();
  }

  async ngOnInit(): Promise<void> {
    await this.loadMonobankAccounts();
    this.loadExistingMapping();
  }

  private async loadMonobankAccounts(): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set(null);

    try {
      const token = await this.state.getApiToken();
      if (!token) {
        this.errorMessage.set(this.translate.instant('screens.monobank.notifications.api_token_not_configured'));
        return;
      }

      const clientInfo = await this.monobankApi.getClientInfo(token);
      this.monobankAccounts.set(clientInfo.accounts);
    } catch (error) {
      this.errorHandler.handle(error, 'screens.monobank.notifications.failed_to_load_accounts', (m) =>
        this.errorMessage.set(m),
      );
    } finally {
      this.loading.set(false);
    }
  }

  private loadExistingMapping(): void {
    const existing = this.state.getAccountMapping();
    if (existing) {
      this.accountMapping.set(this.normalizeAccountMapping(existing));
    }
  }

  onAccountChange(monobankAccountId: string, isoCode: number, appAccountId: number | null): void {
    const current = this.accountMapping();
    if (appAccountId == null) {
      const updated = { ...current };
      delete updated[monobankAccountId];
      this.accountMapping.set(updated);
    } else {
      this.accountMapping.set({
        ...current,
        [monobankAccountId]: {
          accountId: appAccountId,
          currencyCode: this.getAppCurrencyCode(isoCode),
        },
      });
    }
  }

  onSave(): void {
    const mapping = this.normalizeAccountMapping(this.accountMapping());
    this.state.saveAccountMapping(mapping);
    this.dialogRef.close(true);
  }

  private normalizeAccountMapping(mapping: Record<string, MonobankAccountMapping>): Record<string, MonobankAccountMapping> {
    const validMonobankAccounts = new Map(this.monobankAccounts().map((account) => [account.id, account]));
    const validAppAccountIds = new Set(this.appAccounts().map((account) => account.id));

    return Object.fromEntries(
      Object.entries(mapping).flatMap(([monobankAccountId, value]) => {
        if (!value?.accountId || !validAppAccountIds.has(value.accountId)) return [];

        const monobankAccount = validMonobankAccounts.get(monobankAccountId);
        if (!monobankAccount) return [];

        return [
          [
            monobankAccountId,
            {
              accountId: value.accountId,
              currencyCode: this.getAppCurrencyCode(monobankAccount.currencyCode),
            },
          ] satisfies [string, MonobankAccountMapping],
        ];
      }),
    );
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onDisable(): void {
    this.notifications.confirmDeletion({
      message: this.translate.instant('screens.monobank.notifications.disable_confirmation'),
      acceptLabel: this.translate.instant('common.actions.disable'),
      accept: () => {
        this.state.disableMonobank();
        this.dialogRef.close('disabled');
      },
    });
  }
}
